import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import { Observable, from, mergeMap, of, firstValueFrom } from 'rxjs';
import { DexieDbService } from './dexie-db-service';
import { environment } from '@storees/environments';
import {
  PROFILES_ROUTE,
  NULL_UUID,
} from '@storees/shared-constants';
import { MinimalProfile } from '../profile-service';
import { StoreesBackendService } from '../storees-backend.service';
import { VoiceCloneService } from '../voice-clone.service';
import { DexieNarratorsService } from './dexie-narrators.service';
import urlJoin from 'url-join';
import type { components } from '@storees/api-types';
import { NarratorVoice } from '@storees/api-types';
import {
  AsyncTimeoutError,
  promiseWithTimeout,
  STOREES_BACKEND_HTTP_TIMEOUT_MS,
} from '../../utils/async-timeout';

type Profile = components['schemas']['Profile'];

const { apiBaseUrl, defaultVoice } = environment;

/** Options for {@link DexieProfilesService.syncProfiles}. */
export type SyncProfilesOptions = {
  /**
   * When false, skip reconciling recent/favorite story lists with the server (avoids slow PUTs).
   * Call {@link DexieProfilesService.syncProfileStoryLists} before showing story rails from server truth.
   * @default true
   */
  storyLists?: boolean;
};

/** Options for {@link DexieProfilesService.addProfile}. */
export type AddProfileOptions = {
  /** When true, block until {@link syncProfiles} finishes. Defaults to false so UI is not stuck on HTTP. */
  awaitSync?: boolean;
  /** Passed through to {@link syncProfiles} when a background/full sync runs. */
  storyLists?: boolean;
};

@Injectable({
  providedIn: 'root',
})
export class DexieProfilesService {
  private http = inject(HttpClient);
  private db = inject(DexieDbService);
  private storeesBackendService = inject(StoreesBackendService);
  private voiceCloneService = inject(VoiceCloneService);
  private dexieNarratorsService = inject(DexieNarratorsService);

  /** Serializes profile sync so concurrent callers share one run (like narrator sync). */
  private profileSyncQueue: Promise<void> = Promise.resolve();

  private getHeaders(jwtToken: string): HttpHeaders {
    return new HttpHeaders({
      'Authorization': `Bearer ${jwtToken}`,
      'Content-Type': 'application/json'
    });
  }

  async syncProfiles(jwtToken: string, options?: SyncProfilesOptions): Promise<void> {
    if (!jwtToken?.trim()) {
      console.warn('Dexie Profiles Service: Skipping profile sync (missing JWT)');
      return;
    }

    const queuedSync = this.profileSyncQueue
      .catch(() => undefined)
      .then(() =>
        promiseWithTimeout(
          this.performSyncProfiles(jwtToken, options),
          STOREES_BACKEND_HTTP_TIMEOUT_MS,
          'DexieProfilesService.syncProfiles'
        )
      );

    this.profileSyncQueue = queuedSync;
    return queuedSync;
  }

  private async performSyncProfiles(
    jwtToken: string,
    options?: SyncProfilesOptions
  ): Promise<void> {
    const syncStoryLists = options?.storyLists !== false;
    try {
      console.log('Dexie Profiles Service: Syncing profiles...');
      const localProfiles = await this.db.getProfiles();

      const headers = this.getHeaders(jwtToken);

      // First, get server profiles to compare
      let serverProfiles: Profile[];
      try {
        serverProfiles = await this.fetchServerProfiles(headers);
      } catch (error) {
        if (error instanceof AsyncTimeoutError) {
          console.warn(
            'Dexie Profiles Service: Profile list fetch timed out; continuing with local profiles.',
            error.message
          );
          return;
        }
        // Handle HTTP errors gracefully (e.g., 429 rate limit, network issues)
        if (error instanceof HttpErrorResponse) {
          console.error(`Dexie Profiles Service: Failed to fetch profiles from server (${error.status}):`, error.message);
          if (error.status === 429) {
            console.warn('Dexie Profiles Service: Rate limit exceeded. Will retry on next sync.');
          }
        } else {
          console.error('Dexie Profiles Service: Unexpected error fetching profiles:', error);
        }
        // Return early - app continues with local profiles
        return;
      }

      if (!serverProfiles || serverProfiles.length === 0) {
        console.log('Dexie Profiles Service: No profiles found on server.');
      }

      // Create maps for efficient lookup
      const serverProfilesMap = new Map(serverProfiles.map(profile => [profile.id, profile]));
      const localProfilesMap = new Map(localProfiles.map(profile => [profile.id, profile]));

      // Process local profiles
      for (const localProfile of localProfiles) {
        if (localProfile.id === NULL_UUID) {
          continue;
        }

        const serverProfile = serverProfilesMap.get(localProfile.id);

        try {
          const syncPayload = await this.buildSyncPayload(localProfile, serverProfile);
          if (!syncPayload) {
            console.warn(
              'Dexie Profiles Service: Skipping profile sync until narrator is resolved',
              { profileId: localProfile.id, narratorId: localProfile.narratorId }
            );
            continue;
          }

          if (syncPayload.narratorId !== localProfile.narratorId && localProfile.id) {
            await this.db.updateProfile(localProfile.id, { narratorId: syncPayload.narratorId });
          }

          if (!serverProfile) {
            // Profile exists locally but not on server - create it
            await firstValueFrom(this.http
              .post<Profile>(urlJoin(apiBaseUrl, PROFILES_ROUTE), syncPayload, { headers }));
          } else {
            // Profile exists both locally and on server - check for conflicts
            const hasLocalChanges = this.hasProfileChanged(localProfile, serverProfile);

            if (hasLocalChanges) {
              await firstValueFrom(this.http
                .put(urlJoin(apiBaseUrl, PROFILES_ROUTE) + `/${localProfile.id}`, syncPayload, { headers }));
            }
          }
        } catch (error) {
          console.error('Error syncing profile:', localProfile.id, error);
        }
      }

      // Add server profiles that don't exist locally
      const profilesToAdd: Profile[] = [];
      for (const serverProfile of serverProfiles) {
        if (!localProfilesMap.has(serverProfile.id)) {
          profilesToAdd.push(serverProfile);
        }
      }

      // Merge server profiles missing locally (upsert so we never duplicate on bulkAdd).
      if (profilesToAdd.length > 0) {
        await this.db.profiles.bulkPut(profilesToAdd.map((profile) => ({ ...profile })));
      }

      // Reconcile recent/favorite lists with server (can be slow — optional for callers that sync lists separately).
      if (syncStoryLists) {
        for (const serverProfile of serverProfiles) {
          if (serverProfile.id) {
            const localProfile = localProfilesMap.get(serverProfile.id);
            if (localProfile) {
              await this.syncRecentStories(localProfile, serverProfile);
              await this.syncFavoriteStories(jwtToken, localProfile, serverProfile);
            }
          }
        }
      }

      console.log('Dexie Profiles Service: Profile sync completed');
    } catch (error) {
      if (error instanceof AsyncTimeoutError) {
        console.warn(
          'Dexie Profiles Service: Profile sync timed out; continuing with local data.',
          error.message
        );
        return;
      }
      // Catch any unexpected errors to prevent app from stopping
      console.error('Dexie Profiles Service: Unexpected error during profile sync:', error);
      // App continues with local data
    }
  }

  private fetchServerProfiles(headers: HttpHeaders): Promise<Profile[]> {
    return promiseWithTimeout(
      firstValueFrom(
        this.http.get<Profile[]>(urlJoin(apiBaseUrl, PROFILES_ROUTE), { headers })
      ),
      STOREES_BACKEND_HTTP_TIMEOUT_MS,
      'DexieProfilesService.fetchServerProfiles'
    );
  }

  /**
   * Fetches profiles from the server and merges recent/favorite story lists into Dexie for existing local profiles.
   * Run after {@link syncProfiles} with `{ storyLists: false }` when you need server-truth lists before UI story rails.
   */
  async syncProfileStoryLists(jwtToken: string): Promise<void> {
    try {
      const localProfiles = await this.db.getProfiles();
      const localProfilesMap = new Map(localProfiles.map((profile) => [profile.id, profile]));
      const headers = this.getHeaders(jwtToken);

      let serverProfiles: Profile[];
      try {
        serverProfiles = await firstValueFrom(
          this.http.get<Profile[]>(urlJoin(apiBaseUrl, PROFILES_ROUTE), { headers })
        );
      } catch (error) {
        if (error instanceof HttpErrorResponse) {
          console.error(
            `Dexie Profiles Service: syncProfileStoryLists fetch failed (${error.status}):`,
            error.message
          );
        } else {
          console.error('Dexie Profiles Service: syncProfileStoryLists unexpected fetch error:', error);
        }
        return;
      }

      if (!serverProfiles?.length) {
        return;
      }

      for (const serverProfile of serverProfiles) {
        if (!serverProfile.id) {
          continue;
        }
        const localProfile = localProfilesMap.get(serverProfile.id);
        if (localProfile) {
          await this.syncRecentStories(localProfile, serverProfile);
          await this.syncFavoriteStories(jwtToken, localProfile, serverProfile);
        }
      }
    } catch (error) {
      console.error('Dexie Profiles Service: Unexpected error during syncProfileStoryLists:', error);
    }
  }

  private async buildSyncPayload(
    localProfile: MinimalProfile,
    serverProfile?: Profile
  ): Promise<Partial<Profile> | null> {
    const narratorId = await this.resolveNarratorIdForSync(
      localProfile.narratorId,
      serverProfile?.narratorId
    );

    if (!narratorId) {
      return null;
    }

    return {
      id: localProfile.id,
      name: localProfile.name,
      birthdate: localProfile.birthdate,
      gender: localProfile.gender,
      eyeColor: localProfile.eyeColor,
      hairColor: localProfile.hairColor,
      hairLength: localProfile.hairLength,
      hairTexture: localProfile.hairTexture,
      skinColor: localProfile.skinColor,
      description: localProfile.description,
      profileImage: localProfile.profileImage,
      imageStyle: localProfile.imageStyle,
      narratorId,
      defaultNarrativeStyle: localProfile.defaultNarrativeStyle ?? null,
    };
  }

  private async resolveNarratorIdForSync(
    narratorId?: string,
    fallbackNarratorId?: string
  ): Promise<string | null> {
    if (this.isUuid(narratorId)) {
      return narratorId;
    }

    if (narratorId) {
      const resolvedNarrator = await this.voiceCloneService.getNarratorForVoiceId(
        narratorId
      );
      if (this.isUuid(resolvedNarrator?.id)) {
        return resolvedNarrator.id;
      }
    }

    if (this.isUuid(fallbackNarratorId)) {
      return fallbackNarratorId;
    }

    return this.resolveDefaultNarratorUuidFromDexie();
  }

  private async resolveDefaultNarratorUuidFromDexie(): Promise<string | null> {
    try {
      const narrators = await firstValueFrom(this.dexieNarratorsService.getNarrators());
      const preferredVoice = (defaultVoice || NarratorVoice.af_heart) as NarratorVoice;
      const preferred =
        narrators.find((n) => n.voice === preferredVoice) ??
        narrators.find((n) => n.voice === NarratorVoice.af_heart) ??
        narrators[0];
      if (this.isUuid(preferred?.id)) {
        return preferred.id;
      }
    } catch (error) {
      console.warn('Dexie Profiles Service: Could not resolve default narrator from Dexie', error);
    }
    return null;
  }

  private isUuid(value?: string | null): value is string {
    return (
      typeof value === 'string' &&
      /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
        value
      )
    );
  }

  private async syncRecentStories(profile: MinimalProfile, serverProfile: Profile): Promise<void> {
    const serverRecentStories = serverProfile.recentStories || [];

    // CRITICAL: Always update the local profile's recentStories with the server's recentStories
    // DO NOT attempt to "merge" or preserve local stories - the server is the source of truth
    // for recent stories. This overwrites local data by design. Do not change this logic.
    if (profile.id) {
      await this.db.updateProfile(profile.id, { recentStories: serverRecentStories });
    }
  }

  private async syncFavoriteStories(jwtToken: string, localProfile: MinimalProfile, serverProfile: Profile): Promise<void> {
    if (localProfile.id) {
      const localFavoriteStories = localProfile.favoriteStories || [];
      const serverFavoriteStories = serverProfile.favoriteStories || [];
      let favoriteStoriesChanged = false;

      // Create arrays of story IDs for efficient comparison
      const serverStoryIds = serverFavoriteStories.map(story => story.id).filter(id => id);
      const localStoryIds = localFavoriteStories.map(story => story.id).filter(id => id);

      // Find stories that exist in local but not in server (need to mark as favorite)
      for (const story of localFavoriteStories) {
        if (story.id && !serverStoryIds.includes(story.id)) {
          try {
            await this.storeesBackendService.putFavoriteStory(jwtToken, story.id, localProfile.id, true);
            // Add to server profile's favoriteStories
            if (!serverProfile.favoriteStories) {
              serverProfile.favoriteStories = [];
            }
            serverProfile.favoriteStories.push(story);
            favoriteStoriesChanged = true;
          } catch (error) {
            console.error('Error marking story as favorite:', story, error);
          }
        }
      }

      // Find stories that exist in server but not in local (need to mark as not favorite)
      for (const story of serverFavoriteStories) {
        if (story.id && !localStoryIds.includes(story.id)) {
          try {
            await this.storeesBackendService.putFavoriteStory(jwtToken, story.id, localProfile.id, false);
            // Remove from server profile's favoriteStories
            const index = serverProfile.favoriteStories?.findIndex(s => s.id === story.id);
            if (index !== undefined && index > -1 && serverProfile.favoriteStories) {
              serverProfile.favoriteStories.splice(index, 1);
              favoriteStoriesChanged = true;
            }
          } catch (error) {
            console.error('Error unmarking story as favorite:', story, error);
          }
        }
      }

      // Update only the favoriteStories property in the local database if changes were made
      if (favoriteStoriesChanged) {
        await this.db.updateProfile(localProfile.id, { favoriteStories: serverProfile.favoriteStories });
      }
    }
  }

  private hasProfileChanged(localProfile: MinimalProfile, serverProfile: MinimalProfile): boolean {
    // Get all property keys from the MinimalProfile object
    const localKeys = Object.keys(localProfile) as (keyof MinimalProfile)[];
    const serverKeys = Object.keys(serverProfile) as (keyof MinimalProfile)[];

    // Get all unique keys from both objects
    const allKeys = new Set([...localKeys, ...serverKeys]);

    // Compare each property dynamically
    for (const key of allKeys) {
      // Skip recentStories and favoriteStories when checking if profile has changed
      if (key === 'recentStories' || key === 'favoriteStories') {
        continue;
      }

      const localValue = localProfile[key];
      const serverValue = serverProfile[key];

      // Handle null/undefined comparisons for other properties
      if (localValue === null && serverValue === null) {
        continue;
      }
      if (localValue === undefined && serverValue === undefined) {
        continue;
      }
      if (localValue === null || localValue === undefined ||
          serverValue === null || serverValue === undefined) {
        if (localValue !== serverValue && localValue !== undefined && serverValue !== undefined) {
          return true;
        }
        continue;
      }

      // Handle arrays (other than recentStories/favoriteStories)
      if (Array.isArray(localValue) && Array.isArray(serverValue)) {
        if (localValue.length !== serverValue.length) {
          return true;
        }
        // Deep comparison of array contents
        if (JSON.stringify(localValue) !== JSON.stringify(serverValue)) {
          return true;
        }
        continue;
      }

      // Handle objects (like narrator, user)
      if (typeof localValue === 'object' && typeof serverValue === 'object') {
        if (JSON.stringify(localValue) !== JSON.stringify(serverValue)) {
          return true;
        }
        continue;
      }

      // Handle primitive values
      if (localValue !== serverValue) {
        return true;
      }
    }

    return false;
  }

  private profilesAreDifferent(profile1: MinimalProfile, profile2: MinimalProfile): boolean {
    // Simple comparison to check if profiles are different
    return JSON.stringify(profile1) !== JSON.stringify(profile2);
  }

  getProfiles(): Observable<MinimalProfile[]> {
    return from(this.db.getProfiles());
  }

  async profileCount(): Promise<number> {
    return this.db.getProfiles().then(profiles => profiles.length);
  }

  getProfile(id: string): Observable<MinimalProfile | undefined> {
    return from(this.db.getProfile(id));
  }

  addProfile(
    jwtToken: string,
    profile: MinimalProfile,
    options?: AddProfileOptions
  ): Observable<string> {
    const awaitSync = options?.awaitSync === true;
    const syncOptions: SyncProfilesOptions = {
      storyLists: options?.storyLists,
    };

    return from(this.db.addProfile(profile)).pipe(
      mergeMap(async (id) => {
        const syncWork = jwtToken?.trim()
          ? this.syncProfiles(jwtToken, syncOptions)
          : Promise.resolve();

        if (awaitSync) {
          try {
            await syncWork;
          } catch (error) {
            console.error('Error syncing profiles after add:', error);
            throw error;
          }
        } else {
          void syncWork.catch((error) => {
            console.error('Error syncing profiles after add (background):', error);
          });
        }
        return id;
      })
    );
  }

  updateProfile(id: string, jwtToken: string, updates: Partial<Profile>): Observable<Observable<undefined>> {
    return from(this.db.updateProfile(id, updates)).pipe(
      mergeMap(async () => {
        await this.syncProfiles(jwtToken);
        return of(undefined);
      })
    );
  }

  async deleteProfile(id: string, jwtToken: string): Promise<Observable<Observable<undefined>>> {
    const headers = this.getHeaders(jwtToken);
    return from(this.db.deleteProfile(id)).pipe(
      mergeMap(async () => {
        await firstValueFrom(this.http
          .delete(urlJoin(apiBaseUrl, PROFILES_ROUTE) + `/${id}`, { headers, body: {} }));
        return of(undefined);
      })
    );
  }

  /**
   * Method to clear the local db profile and not try to delete them from the server
   */
  async deleteLocalProfileOnly(id: string) {
    await this.db.deleteProfile(id);
    console.log('Dexie Profiles Service: Deleted local profile: ', id);
  }
}
